/**
 * Hook to determine whether the initial data sync has completed.
 *
 * Checks the 4 core sync job types (structure, reports, search-terms, products)
 * against the sync status response. Used by onboarding and dashboard to decide
 * whether to show sync progress UI or normal content.
 *
 * The "initial-backfill" orchestrator job is excluded — it's a parent that
 * fans out child jobs; each child writes its own sync_runs row.
 */
import type { SyncStatusResponse } from "@adpilot/shared";

const INITIAL_SYNC_TYPES = [
  "sync:structure",
  "sync:reports-daily",
  "sync:search-terms",
  "sync:products",
] as const;

interface InitialSyncStatus {
  /** Number of sync types that have completed successfully */
  completed: number;
  /** Total number of sync types checked (always 4) */
  total: number;
  /** True when all 4 sync types have a "success" record */
  isComplete: boolean;
  /** True when initial sync still has work running */
  isInitialSync: boolean;
  /** True when at least one sync type has no record at all (never started) */
  hasNotStarted: boolean;
}

export function useInitialSyncStatus(
  syncStatus: SyncStatusResponse | undefined,
): InitialSyncStatus {
  const runs = syncStatus?.runs ?? [];
  const byType = new Map(runs.map((r) => [r.jobType, r]));

  const completed = INITIAL_SYNC_TYPES.filter(
    (type) => byType.get(type)?.status === "success",
  ).length;
  const total = INITIAL_SYNC_TYPES.length;
  const isComplete = completed === total;
  const isInitialSync = !isComplete && Boolean(syncStatus?.running);
  const hasNotStarted = INITIAL_SYNC_TYPES.some((type) => !byType.has(type));

  return { completed, total, isComplete, isInitialSync, hasNotStarted };
}
